// Site-wide JSON-LD nodes — emitted once in the root layout so every
// page (not just share surfaces) has a baseline of structured data.
//
//   - Organization: tells Google "Morfi Markets" is a brand entity, ties
//     the X / GitHub social profiles to it via `sameAs`. Powers
//     the brand panel in Google Search.
//   - WebSite: declares the site root + an optional SearchAction for
//     Google's Sitelinks Search Box. Even if we never render a search
//     URL, declaring the entity helps crawlers understand the site
//     hierarchy.
//
// Both nodes use a stable `@id` (the canonical site URL with a fragment)
// so other JSON-LD blocks on share surfaces can `@reference` them via
// `publisher: { '@id': ... }` without duplicating the full Organization
// payload on every page.
//
// All URLs come from `NEXT_PUBLIC_SITE_URL` via `resolveCanonicalOrigin`
// — never hardcoded. Computed lazily so each call uses the env value at
// request time (matters in tests / multi-tenant fixtures).

import copy from '../copy';
import { resolveCanonicalOrigin } from '../origin';
import type { JsonLdNode } from './types';

const SITE_NAME: string = copy.site.name;
const LOGO_PATH = '/icons/morfi-logomark.svg';

/** Stable `@id` fragments — combined with the env-driven origin at
 *  builder call time. */
const ORGANIZATION_FRAGMENT = '/#organization';
const WEBSITE_FRAGMENT = '/#website';

const SOCIAL_PROFILES: readonly string[] = [
  'https://x.com/morfimarkets',
  'https://github.com/Morfi-Markets',
];

/** Resolves at call time so tests / per-environment builds pick up the
 *  current `NEXT_PUBLIC_SITE_URL` rather than a frozen module-init value. */
export function organizationId(): string {
  return `${resolveCanonicalOrigin()}${ORGANIZATION_FRAGMENT}`;
}

export function websiteId(): string {
  return `${resolveCanonicalOrigin()}${WEBSITE_FRAGMENT}`;
}

export function buildOrganizationJsonLd(): JsonLdNode {
  const origin = resolveCanonicalOrigin();
  return {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    '@id': `${origin}${ORGANIZATION_FRAGMENT}`,
    name: SITE_NAME,
    alternateName: copy.site.shortName,
    url: origin,
    logo: {
      '@type': 'ImageObject',
      url: `${origin}${LOGO_PATH}`,
      width: 512,
      height: 512,
    },
    // Duplicate of `logo` as a top-level `image` field. Schema.org accepts
    // `logo` for Organization, but several validators (tryunfurl,
    // social-graph crawlers that don't follow `@id` references) only score
    // a top-level `image` property. Setting both is harmless and broadens
    // crawler coverage.
    image: `${origin}${LOGO_PATH}`,
    sameAs: [...SOCIAL_PROFILES],
    description: copy.site.description,
  };
}

export function buildWebSiteJsonLd(): JsonLdNode {
  const origin = resolveCanonicalOrigin();
  // No `potentialAction` SearchAction: search lives in the global SearchModal
  // (cmd+k) — there is no `/search?q=` route to advertise to Google's sitelinks
  // search box. Re-add only when a real search results route exists.
  return {
    '@context': 'https://schema.org',
    '@type': 'WebSite',
    '@id': `${origin}${WEBSITE_FRAGMENT}`,
    name: SITE_NAME,
    url: origin,
    publisher: { '@id': `${origin}${ORGANIZATION_FRAGMENT}` },
    inLanguage: 'en',
  };
}

/** Convenience: both site-wide nodes for the root layout. */
export function buildSiteJsonLd(): JsonLdNode[] {
  return [buildOrganizationJsonLd(), buildWebSiteJsonLd()];
}
